import { useState, useEffect } from "react"
import { Check } from "lucide-react"
import { cn } from "@/lib/utils"
import pianoCss from "../styles/piano.css?raw"
import dashboardCss from "../styles/dashboard.css?raw"
import ctrlCss from "../styles/ctrl.css?raw"

export type ThemePreset = "custom" | "piano" | "dashboard" | "ctrl"

// ── Google Fonts injected on demand ──────────────────────────────────────────
const FONT_URLS: Partial<Record<ThemePreset, string>> = {
  piano:
    "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap",
  dashboard:
    "https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Outfit:wght@300;400;500;600&display=swap",
  ctrl:
    "https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Space+Grotesk:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700&display=swap",
}

function injectFont(preset: ThemePreset) {
  const id = `theme-preset-font-${preset}`
  const url = FONT_URLS[preset]
  if (!url || document.getElementById(id)) return
  const link = document.createElement("link")
  link.id = id
  link.rel = "stylesheet"
  link.href = url
  document.head.appendChild(link)
}

// ── Per-preset raw CSS bundles ────────────────────────────────────────────────
const PRESET_CSS: Partial<Record<ThemePreset, string>> = {
  piano: pianoCss,
  dashboard: dashboardCss,
  ctrl: ctrlCss,
}

// ── HMR: re-inject CSS immediately when a theme file changes in dev ───────────
if (import.meta.hot) {
  const reapply = (key: keyof typeof PRESET_CSS, m: { default: string } | undefined) => {
    if (m) PRESET_CSS[key] = m.default
    const saved = localStorage.getItem("theme-preset") as ThemePreset | null
    if (saved) applyPreset(saved)
  }
  import.meta.hot.accept("../styles/ctrl.css?raw", (m) => reapply("ctrl", m as any))
  import.meta.hot.accept("../styles/piano.css?raw", (m) => reapply("piano", m as any))
  import.meta.hot.accept("../styles/dashboard.css?raw", (m) => reapply("dashboard", m as any))
}

function injectPresetCss(preset: ThemePreset) {
  const existing = document.getElementById("theme-preset-styles")
  if (existing) existing.remove()
  const css = PRESET_CSS[preset]
  if (!css) return
  const style = document.createElement("style")
  style.id = "theme-preset-styles"
  style.textContent = css
  document.head.appendChild(style)
}

// ── Apply a complete preset ───────────────────────────────────────────────────
export function applyPreset(preset: ThemePreset) {
  const root = document.documentElement
  root.setAttribute("data-preset", preset)
  window.dispatchEvent(new CustomEvent("theme-preset-change", { detail: preset }))

  if (preset !== "custom") {
    root.classList.add("dark")
    injectFont(preset)
  }

  injectPresetCss(preset)

  if (preset === "piano") {
    // Near-black monochromatic luxury — inspired by editorial/newspaper aesthetics
    root.style.setProperty("--background", "0 0% 3.5%")
    root.style.setProperty("--foreground", "0 0% 94%")
    root.style.setProperty("--card", "0 0% 5.9%")
    root.style.setProperty("--card-foreground", "0 0% 94%")
    root.style.setProperty("--popover", "0 0% 5.9%")
    root.style.setProperty("--popover-foreground", "0 0% 94%")
    root.style.setProperty("--primary", "0 0% 77%")          // silver
    root.style.setProperty("--primary-foreground", "0 0% 5%")
    root.style.setProperty("--secondary", "0 0% 9%")
    root.style.setProperty("--secondary-foreground", "0 0% 77%")
    root.style.setProperty("--muted", "0 0% 8%")
    root.style.setProperty("--muted-foreground", "0 0% 52%")
    root.style.setProperty("--accent", "0 0% 9%")
    root.style.setProperty("--accent-foreground", "0 0% 94%")
    root.style.setProperty("--destructive", "0 40% 40%")
    root.style.setProperty("--destructive-foreground", "0 0% 94%")
    root.style.setProperty("--border", "0 0% 10%")
    root.style.setProperty("--input", "0 0% 10%")
    root.style.setProperty("--ring", "0 0% 77%")
    root.style.setProperty("--radius", "0.19rem")             // 3px — ultra minimal
    root.style.setProperty("--chart-1", "0 0% 77%")
    root.style.setProperty("--chart-2", "0 0% 62%")
    root.style.setProperty("--chart-3", "0 0% 47%")
    root.style.setProperty("--chart-4", "0 0% 33%")
    root.style.setProperty("--chart-5", "0 0% 20%")
    root.style.setProperty("--sidebar-background", "0 0% 5.9%")
    root.style.setProperty("--sidebar-foreground", "0 0% 94%")
    root.style.setProperty("--sidebar-primary", "0 0% 77%")
    root.style.setProperty("--sidebar-primary-foreground", "0 0% 5%")
    root.style.setProperty("--sidebar-accent", "0 0% 8%")
    root.style.setProperty("--sidebar-accent-foreground", "0 0% 94%")
    root.style.setProperty("--sidebar-border", "0 0% 10%")
    root.style.setProperty("--sidebar-ring", "0 0% 77%")

  } else if (preset === "dashboard") {
    // Deep navy tech — blue accent with purple secondary, grid lines, glow orbs
    root.style.setProperty("--background", "222 52% 6%")
    root.style.setProperty("--foreground", "225 60% 92%")
    root.style.setProperty("--card", "222 47% 9%")
    root.style.setProperty("--card-foreground", "225 60% 92%")
    root.style.setProperty("--popover", "222 47% 9%")
    root.style.setProperty("--popover-foreground", "225 60% 92%")
    root.style.setProperty("--primary", "217 91% 64%")        // #4f8ef7 blue
    root.style.setProperty("--primary-foreground", "222 52% 6%")
    root.style.setProperty("--secondary", "262 83% 58%")      // #8b5cf6 purple
    root.style.setProperty("--secondary-foreground", "225 60% 92%")
    root.style.setProperty("--muted", "222 40% 12%")
    root.style.setProperty("--muted-foreground", "225 30% 55%")
    root.style.setProperty("--accent", "222 40% 15%")
    root.style.setProperty("--accent-foreground", "225 60% 92%")
    root.style.setProperty("--destructive", "0 84% 60%")
    root.style.setProperty("--destructive-foreground", "225 60% 92%")
    root.style.setProperty("--border", "222 35% 14%")
    root.style.setProperty("--input", "222 35% 14%")
    root.style.setProperty("--ring", "217 91% 64%")
    root.style.setProperty("--radius", "0.5rem")              // 8px
    root.style.setProperty("--chart-1", "217 91% 64%")
    root.style.setProperty("--chart-2", "262 83% 58%")
    root.style.setProperty("--chart-3", "166 76% 51%")
    root.style.setProperty("--chart-4", "38 92% 50%")
    root.style.setProperty("--chart-5", "0 84% 60%")
    root.style.setProperty("--sidebar-background", "222 52% 5%")
    root.style.setProperty("--sidebar-foreground", "225 60% 92%")
    root.style.setProperty("--sidebar-primary", "217 91% 64%")
    root.style.setProperty("--sidebar-primary-foreground", "222 52% 6%")
    root.style.setProperty("--sidebar-accent", "217 91% 30%")   // ~rgba(79,142,247,0.1) dark
    root.style.setProperty("--sidebar-accent-foreground", "217 91% 64%")  // accent blue text
    root.style.setProperty("--sidebar-border", "222 35% 12%")
    root.style.setProperty("--sidebar-ring", "217 91% 64%")

  } else if (preset === "ctrl") {
    // Deep space navy + cyan neon HUD aesthetic
    root.style.setProperty("--background", "222 90% 2%")       // #020816
    root.style.setProperty("--foreground", "194 100% 96%")     // #e8f8ff
    root.style.setProperty("--card", "220 73% 6%")             // #060f1e
    root.style.setProperty("--card-foreground", "194 100% 96%")
    root.style.setProperty("--popover", "220 73% 6%")
    root.style.setProperty("--popover-foreground", "194 100% 96%")
    root.style.setProperty("--primary", "194 100% 50%")        // #00d2ff cyan
    root.style.setProperty("--primary-foreground", "222 90% 2%")
    root.style.setProperty("--secondary", "280 91% 65%")       // #a855f7 purple
    root.style.setProperty("--secondary-foreground", "194 100% 96%")
    root.style.setProperty("--muted", "221 60% 8%")
    root.style.setProperty("--muted-foreground", "194 40% 45%")
    root.style.setProperty("--accent", "194 100% 6%")
    root.style.setProperty("--accent-foreground", "194 100% 96%")
    root.style.setProperty("--destructive", "341 100% 58%")    // #ff2d6b
    root.style.setProperty("--destructive-foreground", "194 100% 96%")
    root.style.setProperty("--border", "194 100% 8%")          // rgba(0,210,255,0.08)
    root.style.setProperty("--input", "194 100% 8%")
    root.style.setProperty("--ring", "194 100% 50%")
    root.style.setProperty("--radius", "0.25rem")              // 4px — sharp HUD
    root.style.setProperty("--chart-1", "194 100% 50%")        // cyan
    root.style.setProperty("--chart-2", "280 91% 65%")         // purple
    root.style.setProperty("--chart-3", "159 100% 45%")        // success green
    root.style.setProperty("--chart-4", "42 100% 36%")         // warning yellow
    root.style.setProperty("--chart-5", "341 100% 58%")        // danger red
    root.style.setProperty("--sidebar-background", "220 73% 4%")
    root.style.setProperty("--sidebar-foreground", "194 100% 96%")
    root.style.setProperty("--sidebar-primary", "194 100% 50%")
    root.style.setProperty("--sidebar-primary-foreground", "222 90% 2%")
    root.style.setProperty("--sidebar-accent", "194 100% 10%")        // hover bg
    root.style.setProperty("--sidebar-accent-foreground", "194 100% 50%") // active cyan text
    root.style.setProperty("--sidebar-border", "194 100% 8%")
    root.style.setProperty("--sidebar-ring", "194 100% 50%")
  }
}

// ── Preset metadata ───────────────────────────────────────────────────────────
const presets: {
  id: ThemePreset
  name: string
  description: string
  preview: React.ReactNode
}[] = [
  {
    id: "custom",
    name: "Custom",
    description: "Suas escolhas de cor e fundo",
    preview: (
      <div
        className="w-full h-full rounded-[3px] p-1.5 flex flex-col gap-1"
        style={{ background: "#18181b" }}
      >
        <div className="h-2 w-8 rounded-sm" style={{ background: "#3b82f6" }} />
        <div className="flex gap-1 flex-1">
          <div className="w-1/4 h-full rounded-sm" style={{ background: "rgba(255,255,255,0.08)" }} />
          <div className="flex-1 h-full rounded-sm" style={{ background: "rgba(255,255,255,0.05)" }} />
        </div>
        <div className="h-1.5 w-full rounded-sm" style={{ background: "rgba(59,130,246,0.35)" }} />
      </div>
    ),
  },
  {
    id: "piano",
    name: "Piano",
    description: "Luxo editorial monochrome",
    preview: (
      <div
        className="w-full h-full rounded-[3px] p-1.5 flex flex-col gap-1 overflow-hidden relative"
        style={{ background: "#090909" }}
      >
        {/* accent line */}
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background:
              "linear-gradient(90deg,transparent,rgba(196,196,196,0.7) 40%,rgba(255,255,255,0.9) 50%,rgba(196,196,196,0.7) 60%,transparent)",
          }}
        />
        {/* sidebar + content */}
        <div className="flex gap-1 flex-1">
          <div className="w-6 h-full rounded-sm" style={{ background: "#0f0f0f", border: "1px solid rgba(255,255,255,0.07)" }} />
          <div className="flex-1 flex flex-col gap-1">
            <div className="h-1.5 w-10 rounded-sm" style={{ background: "#c4c4c4", opacity: 0.6 }} />
            <div className="flex gap-0.5 flex-1">
              {[40, 60, 45, 75].map((_h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-sm"
                  style={{ background: `rgba(196,196,196,${0.06 + i * 0.02})`, height: "100%" }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "dashboard",
    name: "Dashboard",
    description: "Tech azul com brilho moderno",
    preview: (
      <div
        className="w-full h-full rounded-[3px] p-1.5 flex flex-col gap-1 overflow-hidden relative"
        style={{ background: "#070b14" }}
      >
        {/* grid lines */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(79,142,247,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(79,142,247,0.06) 1px,transparent 1px)",
            backgroundSize: "12px 12px",
          }}
        />
        {/* blue orb */}
        <div
          className="absolute"
          style={{
            width: 40, height: 40,
            borderRadius: "50%",
            background: "rgba(79,142,247,0.18)",
            filter: "blur(14px)",
            top: -12, right: -4,
          }}
        />
        {/* sidebar + content */}
        <div className="flex gap-1 flex-1 relative z-10">
          <div className="w-6 h-full rounded-sm" style={{ background: "#090e1c", border: "1px solid rgba(255,255,255,0.07)" }} />
          <div className="flex-1 flex flex-col gap-1">
            <div className="h-1.5 w-10 rounded-sm" style={{ background: "#4f8ef7", opacity: 0.8 }} />
            <div className="flex gap-0.5 flex-1">
              {[40, 70, 50, 85].map((_h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-sm"
                  style={{
                    background: i % 2 === 0 ? "rgba(79,142,247,0.15)" : "rgba(139,92,246,0.12)",
                    height: "100%",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "ctrl",
    name: "CTRL",
    description: "HUD sci-fi, cyan neon + mono",
    preview: (
      <div
        className="w-full h-full rounded-[3px] p-1.5 flex flex-col gap-1 overflow-hidden relative"
        style={{ background: "#020816" }}
      >
        {/* grid lines */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(0,210,255,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(0,210,255,0.04) 1px,transparent 1px)",
            backgroundSize: "10px 10px",
          }}
        />
        {/* ticker bar */}
        <div
          className="absolute top-0 left-0 right-0"
          style={{ height: 4, background: "rgba(0,210,255,0.6)" }}
        />
        {/* sidebar + content */}
        <div className="flex gap-1 flex-1 relative z-10" style={{ marginTop: 4 }}>
          <div className="w-6 h-full rounded-sm" style={{ background: "#060f1e", border: "1px solid rgba(0,210,255,0.12)" }} />
          <div className="flex-1 flex flex-col gap-1">
            <div className="h-1.5 w-10 rounded-sm" style={{ background: "#00d2ff", opacity: 0.7, boxShadow: "0 0 4px rgba(0,210,255,0.4)" }} />
            <div className="flex gap-0.5 flex-1">
              {[40, 70, 50, 85].map((_h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-sm"
                  style={{
                    background: i === 1 ? "rgba(0,210,255,0.14)" : i === 3 ? "rgba(168,85,247,0.12)" : "rgba(0,210,255,0.06)",
                    border: "1px solid rgba(0,210,255,0.10)",
                    height: "100%",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    ),
  },
]

// ── Component ─────────────────────────────────────────────────────────────────
export function ThemePresetSelector({
  onPresetChange,
}: {
  onPresetChange?: (preset: ThemePreset) => void
}) {
  const [current, setCurrent] = useState<ThemePreset>("custom")

  useEffect(() => {
    const saved = (localStorage.getItem("theme-preset") as ThemePreset) || "custom"
    setCurrent(saved)
    applyPreset(saved)
  }, [])

  const handleSelect = (preset: ThemePreset) => {
    setCurrent(preset)
    localStorage.setItem("theme-preset", preset)
    applyPreset(preset)
    onPresetChange?.(preset)
  }

  return (
    <div className="grid grid-cols-3 gap-3">
      {presets.map((preset) => (
        <button
          key={preset.id}
          onClick={() => handleSelect(preset.id)}
          className={cn(
            "group relative flex flex-col gap-2 rounded-lg border p-1.5 text-left transition-all hover:border-primary/50 hover:bg-muted/30",
            current === preset.id
              ? "border-primary bg-primary/5 shadow-sm shadow-primary/10"
              : "border-border"
          )}
        >
          {/* mini preview */}
          <div className="h-16 w-full overflow-hidden rounded-[5px] border border-white/5">
            {preset.preview}
          </div>

          {/* label */}
          <div className="px-0.5 pb-0.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold">{preset.name}</span>
              {current === preset.id && (
                <Check className="h-3 w-3 text-primary" />
              )}
            </div>
            <span className="text-[10px] text-muted-foreground leading-tight">
              {preset.description}
            </span>
          </div>
        </button>
      ))}
    </div>
  )
}
